import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { AccessLevel, SITES_MODULE } from '../lib/permissions';
import { sitesApiAvailable } from '../lib/sites';
import { formatPendingCount, useCreatorPendingCount } from '../lib/creatorPendingCount';
import { EnvSwitch, EnvBadge } from './EnvSwitch';
import { useSupportUnread } from '../lib/supportUnread';

/** Filtered by permission — people only see doors they can open. */
const NAV: Array<{ to: string; label: string; module: string; level?: AccessLevel }> = [
  { to: '/', label: 'Overview', module: 'overview' },
  { to: '/funnel', label: 'User Funnel', module: 'overview' },
  { to: '/accounts', label: 'Individuals', module: 'accounts' },
  { to: '/agency', label: 'Agencies', module: 'accounts' },
  { to: '/partners', label: 'Partners', module: 'partners' },
  { to: '/sites', label: 'Sites & Sandboxes', module: 'sites' },
  { to: '/transfers', label: 'Transfers', module: 'sites' },
  { to: '/invites', label: 'Invites', module: 'invites' },
  { to: '/emails', label: 'Emails', module: 'emails' },
  { to: '/support', label: 'Support', module: 'support' },
  { to: '/support/recipients', label: 'Support Recipients', module: 'support_manage' },
  { to: '/creator-approvals', label: 'Creator Approvals', module: 'creators' },
  { to: '/coupons', label: 'Coupons', module: 'coupons' },
  { to: '/plans', label: 'Plans', module: 'coupons' },
  { to: '/bookings', label: 'Bookings', module: 'bookings' },
  { to: '/announcements', label: 'Features', module: 'announcements' },
  { to: '/finances', label: 'Finances', module: 'finances' },
  { to: '/users', label: 'Users', module: 'portal_users' },
  { to: '/roles', label: 'Roles', module: 'portal_users' },
  // Engineering tools sit at the very end.
  { to: '/prompts', label: 'Prompts', module: 'prompts' },
  { to: '/releases', label: 'Releases', module: 'releases' },
];

/**
 * A nav item owns every path beneath it (`/support/123` still lights up
 * "Support"), except where another nav item is nested under it and claims the
 * current path: on `/support/recipients` only "Support Recipients" is active.
 */
function claimedByNestedItem(to: string, pathname: string): boolean {
  return NAV.some((other) =>
    other.to !== to && other.to.startsWith(to + '/') &&
    (pathname === other.to || pathname.startsWith(other.to + '/')));
}

export default function Layout() {
  const { user, signOut, can } = useAuth();
  // The Sites section ships ahead of/behind its backend route depending on the
  // deploy order: hide the entry when the connected backend answers 404 for it.
  // Unknown (still probing) and any non-404 outcome keep it visible.
  const wantsSites = can(SITES_MODULE, 'read');
  const [sitesApi, setSitesApi] = useState<boolean | null>(null);
  useEffect(() => {
    if (!wantsSites) return;
    let live = true;
    void sitesApiAvailable().then((ok) => { if (live) setSitesApi(ok); });
    return () => { live = false; };
  }, [wantsSites]);
  const supportUnread = useSupportUnread(can('support', 'read'));
  // Creator submissions waiting for review: a number on the Creator Approvals item.
  const { pathname } = useLocation();
  const pending = formatPendingCount(useCreatorPendingCount(can('creators', 'read'), pathname));
  const visible = NAV.filter((i) => can(i.module, i.level ?? 'read')
    && (i.module !== SITES_MODULE || sitesApi !== false));

  return (
    <div className="shell">
      <EnvBadge />
      <aside className="sidebar">
        <div className="brand"><span className="brand-mark">W</span> WPOS</div>
        <nav>
          {visible.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'}
              className={({ isActive }) => 'nav-item' + (isActive && !claimedByNestedItem(item.to, pathname) ? ' active' : '')}>
              {item.label}
              {item.to === '/support' && supportUnread.ticketIds.length > 0 && (
                <span className="nav-count" data-testid="support-unread-count" aria-label={`${supportUnread.ticketIds.length} tickets with new customer messages`}>
                  {supportUnread.ticketIds.length > 99 ? '99+' : supportUnread.ticketIds.length}
                </span>
              )}
              {item.module === 'creators' && pending && (
                <span className="nav-count" aria-label={`${pending} pending review`}>{pending}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-foot">
          <EnvSwitch />
          <NavLink to="/mcp-tokens" className="btn btn-ghost btn-sm">MCP tokens</NavLink>
          <div className="who"><strong>{user?.fullName}</strong><span>{user?.role?.name ?? '—'}</span></div>
          <button className="btn btn-ghost btn-sm" onClick={signOut}>Sign out</button>
        </div>
      </aside>
      <main className="content"><Outlet /></main>
    </div>
  );
}
